import { useCallback, useEffect, useMemo, useState } from "react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import DataTable from "@/components/DataTable";
import GeneraPrefatturaDialog from "@/components/GeneraPrefatturaDialog";
import PrefatturaDetail, { STATO_PF } from "@/components/PrefatturaDetail";
import api, { apiError } from "@/lib/api";
import { toast } from "sonner";
import { formatDate, formatCurrency } from "@/lib/format";
import { Eye } from "lucide-react";

const PAGE_SIZE = 25;

export default function Prefatture() {
  const [config, setConfig] = useState(null);
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [sortBy, setSortBy] = useState("created_at");
  const [sortDir, setSortDir] = useState("desc");
  const [search, setSearch] = useState("");
  const [filters, setFilters] = useState({});
  const [genOpen, setGenOpen] = useState(false);
  const [detailId, setDetailId] = useState(null);

  useEffect(() => {
    api.get("/prefatture/config").then((r) => setConfig(r.data));
  }, []);

  const load = useCallback(() => {
    setLoading(true);
    const p = { page, page_size: PAGE_SIZE, sort_by: sortBy, sort_dir: sortDir };
    if (search) p.search = search;
    Object.entries(filters).forEach(([k, v]) => v && (p[k] = v));
    api.get("/prefatture", { params: p })
      .then((r) => { setRows(r.data.items); setTotal(r.data.total); })
      .catch((e) => toast.error(apiError(e)))
      .finally(() => setLoading(false));
  }, [page, sortBy, sortDir, search, filters]);

  useEffect(() => { load(); }, [load]);

  const columns = useMemo(() => [
    { key: "numero", label: "Numero", render: (r) => <span className="font-medium text-slate-800">{r.numero}</span> },
    { key: "tipo", label: "Tipo", render: (r) => <Badge variant="outline" className={r.tipo === "attiva" ? "border-sky-300 text-sky-700" : "border-amber-300 text-amber-700"}>{r.tipo === "attiva" ? "Attiva (cliente)" : "Passiva (sub-vettore)"}</Badge> },
    { key: "soggetto_nome", label: "Soggetto", render: (r) => r.soggetto_nome },
    { key: "periodo_dal", label: "Periodo", nowrap: true, render: (r) => `${formatDate(r.periodo_dal)} → ${formatDate(r.periodo_al)}` },
    { key: "n_viaggi", label: "Viaggi", align: "right", sortable: false, render: (r) => r.n_viaggi },
    { key: "imponibile", label: "Imponibile", align: "right", render: (r) => formatCurrency(r.imponibile) },
    { key: "totale", label: "Totale", align: "right", render: (r) => <span className="font-medium">{formatCurrency(r.totale)}</span> },
    { key: "n_warning", label: "Avvisi", align: "right", sortable: false, render: (r) => (r.n_warning ? <span className="text-amber-700 font-medium">{r.n_warning}</span> : "—") },
    { key: "stato", label: "Stato", render: (r) => <Badge variant="outline" className={`capitalize ${STATO_PF[r.stato] || ""}`}>{r.stato}</Badge> },
    { key: "created_at", label: "Creata", nowrap: true, render: (r) => formatDate(r.created_at) },
  ], []);

  const filterFields = useMemo(() => config ? [
    { name: "tipo", label: "Tipo", options: [{ value: "attiva", label: "Attiva" }, { value: "passiva", label: "Passiva" }] },
    { name: "stato", label: "Stato", options: config.stati.map((s) => ({ value: s, label: s })) },
  ] : [], [config]);

  return (
    <div className="space-y-4" data-testid="prefatture-page">
      <DataTable
        testid="prefatture"
        columns={columns}
        rows={rows}
        loading={loading}
        total={total}
        page={page}
        pageSize={PAGE_SIZE}
        onPage={setPage}
        sortBy={sortBy}
        sortDir={sortDir}
        onSort={(k) => { if (sortBy === k) setSortDir((d) => (d === "asc" ? "desc" : "asc")); else { setSortBy(k); setSortDir("asc"); } }}
        search={search}
        onSearch={(v) => { setSearch(v); setPage(1); }}
        filterFields={filterFields}
        filterValues={filters}
        onFilter={(k, v) => { setFilters((f) => ({ ...f, [k]: v })); setPage(1); }}
        canWrite={config?.can_write}
        onNew={() => setGenOpen(true)}
        newLabel="Genera prefattura"
        emptyLabel="Nessuna prefattura. Genera la prima da un periodo di viaggi eseguiti."
        renderActions={(r) => (
          <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => setDetailId(r.id)} data-testid={`prefattura-open-${r.id}`} title="Apri">
            <Eye className="h-4 w-4" />
          </Button>
        )}
      />
      <GeneraPrefatturaDialog open={genOpen} onOpenChange={setGenOpen} onCreated={(pf) => { load(); setDetailId(pf.id); }} />
      <PrefatturaDetail id={detailId} onClose={() => setDetailId(null)} onChanged={load} canWrite={config?.can_write} />
    </div>
  );
}
